import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useContent } from "./content";

const KEY = "capsud_cart_v2";
const Ctx = createContext(null);

const read = () => {
  try {
    const v = JSON.parse(localStorage.getItem(KEY));
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [lines, setLines] = useState(read);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(lines));
  }, [lines]);

  const add = useCallback((id) => {
    setLines((ls) => {
      const i = ls.findIndex((l) => l.id === id && !l.meat && !l.veg);
      if (i >= 0) return ls.map((l, j) => (j === i ? { ...l, qty: Math.min(20, l.qty + 1) } : l));
      return [...ls, { key: `${id}-${Date.now()}`, id, qty: 1, meat: 0, veg: 0 }];
    });
  }, []);
  const update = useCallback((key, patch) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l))), []);
  const remove = useCallback((key) => setLines((ls) => ls.filter((l) => l.key !== key)), []);
  const clear = useCallback(() => setLines([]), []);

  const value = useMemo(() => ({ lines, add, update, remove, clear }), [lines, add, update, remove, clear]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useCart = () => useContext(Ctx);

const priced = (arr, kind) => (Array.isArray(arr) ? arr : []).filter((p) => p.price != null).map((p) => ({ ...p, kind }));

export const useCartTotals = () => {
  const { lines } = useCart();
  const { content } = useContent();
  return useMemo(() => {
    const products = [...priced(content?.pizzas, "pizza"), ...priced(content?.pates, "pates")];
    const pm = Number(content?.supplements?.viande?.price ?? 0);
    const pv = Number(content?.supplements?.legume?.price ?? 0);
    const items = lines
      .map((l) => {
        const product = products.find((p) => p.id === l.id);
        if (!product) return null;
        const unit = Number(product.price) + l.meat * pm + l.veg * pv;
        return { ...l, product, unit, total: unit * l.qty };
      })
      .filter(Boolean);
    return {
      items,
      total: Math.round(items.reduce((s, i) => s + i.total, 0) * 100) / 100,
      count: items.reduce((s, i) => s + i.qty, 0),
      pizzaCount: items.filter((i) => i.product.kind === "pizza").reduce((s, i) => s + i.qty, 0),
    };
  }, [lines, content]);
};
